import React from "react";
import * as R from "ramda";
import { Button, Icon, Popconfirm, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { Draggable } from "react-beautiful-dnd";

import StepConfiguration from "../Step/StepConfiguration";
import { ConditionalWrapper } from "../../../../../components/ConditionalWrapper";
import { getStepDisplayName } from "../selectors";

import styled from "styled-components/macro";

const Wrapper = styled.div`
  position: relative;
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  margin: 0 16px 12px;
  overflow: hidden;
  box-shadow: ${({ editMode }) =>
    editMode
      ? "0 10px 24px rgba(52, 58, 64, 0.08)"
      : "0 4px 14px rgba(52, 58, 64, 0.04)"};
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  ${({ isDragging }) =>
    isDragging &&
    `
    border-radius: 16px;
    background-color: white;
    transform: scale(1.01);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.16);
  `}

  .center {
    flex: 1;
  }

  h2 {
    font-size: 16px;
    font-weight: bold;
    line-height: 1.5;
    text-align: left;
    color: #343a40;
    margin-bottom: 0;
  }

  .details {
    margin: 0;
    padding: 0 24px 20px 64px;
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    justify-content: flex-start;

    > div {
      min-width: 230px;
    }
    h3 {
      font-size: 10px;
      font-weight: normal;
      line-height: 1.6;
      color: #747c84;
      margin-bottom: 4px;
    }
    span {
      font-size: 13px;
      line-height: 1.54;
      color: #343a40;
    }
  }
`;

const StepHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px 16px 24px;
  background: ${({ editMode }) =>
    editMode ? "linear-gradient(180deg, #f8faff 0%, #ffffff 100%)" : "#ffffff"};

  .step-title {
    flex-grow: 1;
    margin: 0;
  }

  .handle {
    align-self: flex-start;
    padding: 8px;
    margin-right: 0;
    border-radius: 10px;
    background-color: rgba(52, 58, 64, 0.06);

    .anticon {
      display: block;
    }
  }
`;

const HeaderContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const StepDetails = styled.div`
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const Buttons = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;

  > * + * {
    margin-left: 8px;
  }
`;

const EditMode = styled.div`
  display: flex;
  align-items: center;
  margin-left: 16px;
  padding: 6px 10px;
  border-radius: 999px;
  background-color: rgba(24, 144, 255, 0.08);
  white-space: nowrap;

  .text {
    margin-right: 8px;
    font-size: 12px;
    font-weight: 600;
    color: #1f5fbf;
  }
`;

const getDocumentRequirementName = (step, t) => {
  const documentName = R.pathOr("", ["config", "documentName"], step);

  if (R.path(["config", "uploadMode"], step) !== "single") {
    return documentName;
  }

  const singleUploadMode = t(
    "opportunity:process.stepTypes.document_upload.uploadModes.single"
  );
  const singleUploadSuffix = ` - ${singleUploadMode}`;

  return documentName.endsWith(singleUploadSuffix)
    ? documentName.slice(0, -singleUploadSuffix.length)
    : documentName;
};

const ProcessStep = ({
  step,
  index,
  isEditing,
  editMode,
  switchEditMode,
  disabled,
  isWizard,
  onSave,
  onRemove,
  updateStep,
  draggable = true,
  allMatches
}) => {
  const { t } = useTranslation(["opportunity"]);

  if (step.type === "test-gh") {
    step.type = "test";
    step.config.source = "gh";
  }

  const isSelectedStep = step.order === 98;
  const isAcceptedStep = step.order === 99;
  const isFixedStep = isSelectedStep || isAcceptedStep;
  const chatbotMessage = R.pathOr("", ["config", "message"], step);
  const documentRequirementName = getDocumentRequirementName(step, t);

  return (
    <ConditionalWrapper
      condition={draggable}
      trueWrapper={children => (
        <Draggable draggableId={step.id} index={index}>
          {(provided, snapshot) => children(provided, snapshot)}
        </Draggable>
      )}
      falseWrapper={children => children({}, {})}
    >
      {(provided, snapshot) => (
        <Wrapper
          isDragging={snapshot.isDragging}
          editMode={editMode}
          ref={provided.innerRef}
          data-test="step"
          {...provided.draggableProps}
          {...(!editMode ? provided.dragHandleProps : {})}
        >
          <StepHeader editMode={editMode}>
            <div className="handle" {...provided.dragHandleProps}>
              <Icon type={draggable ? "menu" : "lock"} />
            </div>
            {step.automaticStep && (
              <Tooltip
                title={t("opportunity:process.stepDetails.automaticStep")}
              >
                <Icon
                  type="thunderbolt"
                  style={{ color: "blue", marginRight: 5 }}
                />
              </Tooltip>
            )}
            <HeaderContent>
              <h2 className="step-title">{getStepDisplayName(step, t)}</h2>
            </HeaderContent>
            <Buttons>
              {editMode ? (
                <EditMode>
                  <span className="text">
                    {t("opportunity:process.processEditor.editMode")}
                  </span>
                  <Popconfirm
                    title={
                      step.unvalidated &&
                      t("opportunity:process.processEditor.sureLeaveEdit")
                    }
                    placement="topLeft"
                    onConfirm={onRemove}
                  >
                    <Button
                      onClick={step.unvalidated ? null : switchEditMode}
                      type="primary"
                      icon="close"
                    />
                  </Popconfirm>
                </EditMode>
              ) : (
                <Button
                  onClick={switchEditMode}
                  disabled={disabled}
                  icon="edit"
                />
              )}

              <Popconfirm
                title={t("opportunity:process.processEditor.sureDelete")}
                onConfirm={onRemove}
                disabled={!onRemove || (isWizard && !editMode && isEditing)}
                okText={t(
                  "opportunity:process.processEditor.deleteStepConfirm"
                )}
                cancelText={t("global:cancel")}
                okButtonProps={{
                  "data-test": "confirm-step-delete"
                }}
                placement="topLeft"
              >
                <Tooltip
                  title={
                    draggable &&
                    !onRemove &&
                    !isWizard &&
                    t(
                      "opportunity:process.processEditor.cannotDeleteStepMatches"
                    )
                  }
                >
                  <Button
                    data-test="delete-step-btn"
                    icon="delete"
                    type="danger"
                    ghost
                    // This button is disabled when editing because it causes a bug where the edited step is saved regardless of the validness of the required inputs
                    disabled={!onRemove || (!editMode && isEditing)}
                  ></Button>
                </Tooltip>
              </Popconfirm>
            </Buttons>
          </StepHeader>
          <StepDetails disabled={disabled}>
            {!editMode && (
              <div className="details">
                {!isFixedStep && (
                  <div>
                    <h3>{t("opportunity:process.processEditor.stepType")}</h3>
                    <span>
                      {t(`opportunity:process.stepTypes.${step.type}.title`)}
                    </span>
                  </div>
                )}
                {isFixedStep && (
                  <div>
                    <h3>{t("opportunity:process.processEditor.stepType")}</h3>
                    <span>
                      {t("opportunity:process.stepDetails.chatbotMessage")}
                    </span>
                  </div>
                )}

                {isFixedStep && chatbotMessage && (
                  <div>
                    <h3>
                      {t("opportunity:process.stepDetails.chatbotMessage")}
                    </h3>
                    <span>{chatbotMessage}</span>
                  </div>
                )}
                {R.path(["config", "users", 0, "name"])(step) && (
                  <div>
                    <h3>
                      {t("opportunity:process.processEditor.interviewer")}
                    </h3>
                    <span>{R.path(["config", "users", 0, "name"])(step)}</span>
                  </div>
                )}
                {step.type === "test" && R.path(["config", "idTest"], step) && (
                  <div>
                    <h3>{t("opportunity:process.processEditor.test")}</h3>
                    <span>
                      {t(
                        `opportunity:process.stepTypes.test-${step.config.source}.${step.config.idTest}.title`
                      )}
                    </span>
                  </div>
                )}
                {step.type === "document_upload" &&
                  documentRequirementName && (
                    <div>
                      <h3>{t("opportunity:process.processEditor.requirement")}</h3>
                      <span>{documentRequirementName}</span>
                    </div>
                  )}
              </div>
            )}
            {editMode && (
              <StepConfiguration
                allMatches={allMatches}
                step={step}
                isWizard={isWizard}
                onSave={onSave}
                onRemove={onRemove}
                updateStep={updateStep}
                chatbotMessageOnly={isAcceptedStep}
              />
            )}
          </StepDetails>
        </Wrapper>
      )}
    </ConditionalWrapper>
  );
};

export default ProcessStep;
